import React from "react";
import { Integration, Product } from "@/types/general";
import { modules_links } from "@components/Dashboard/DashboardPage/subpages/FeedPage/config";
import { useTranslation } from "react-i18next";
import BaseBadge from "@reusables/BaseBadge";
import BaseIntegrationInject from "@reusables/BaseIntegrationInject";
import { Link } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import { Box } from "@mui/material";

type OriginLinkProperties = {
    origin: Product.Augmentations.Prices.Origin;
};

type KeywordMapping = {
    translation: string;
    integrationSlug?: Integration.Components.Slug;
    link?: (id: number) => string;
};

const keywordMappings: Record<Product.Augmentations.Prices.OriginName, KeywordMapping> = {
    adjustment: {
        translation: "adjustment",
    },
    manual: {
        translation: "manual",
    },
    product: {
        translation: "product",
    },
    lime: {
        translation: "product",
        integrationSlug: "lime",
    },
    lime_product: {
        translation: "product",
        integrationSlug: "lime",
    },
    lime_order: {
        translation: "sale_order",
        integrationSlug: "lime",
        link: (id: number) => [modules_links["sale_order"]?.link, id, "details"].join("/"),
    },
    purchase_order: {
        translation: "purchase_order",
        link: (id: number) => [modules_links["purchase_order"]?.link, id, "details"].join("/"),
    },
    sale_order: {
        translation: "sale_order",
        link: (id: number) => [modules_links["sale_order"]?.link, id, "details"].join("/"),
    },
    tripletex: {
        translation: "product",
        integrationSlug: "tripletex",
    },
    twenty_four_seven_office: {
        translation: "product",
        integrationSlug: "twenty_four_seven_office",
    },
    power_office: {
        translation: "product",
        integrationSlug: "poweroffice",
    },
    poweroffice: {
        translation: "product",
        integrationSlug: "poweroffice",
    },
    shipmondo: {
        translation: "product",
        integrationSlug: "shipmondo",
    },
    woocommerce: {
        translation: "product",
        integrationSlug: "woocommerce",
    },
    shopify: {
        translation: "product",
        integrationSlug: "shopify",
    },
    susoft: {
        translation: "product",
        integrationSlug: "susoft",
    },
    reconversion: {
        translation: "reconversion",
    },
    dfi_import: {
        translation: "file_import",
    },
};

export default function OriginLink({ origin }: OriginLinkProperties) {
    const { t } = useTranslation("", {
        keyPrefix: "products.viewEditProduct.tabs.prices.table.origins",
    });

    const mapping = keywordMappings[origin.name];

    // Getting the translation
    const originTranslation = t(`${mapping.translation}`);

    const originContent = mapping.integrationSlug ? (
        <BaseIntegrationInject integration={mapping.integrationSlug}>
            {(integration) => (
                <>
                    <BaseBadge
                        image={<img src={integration?.imageUrl} alt="" width={12} height={12} />}
                    >
                        {integration?.name ?? "unknown"}
                        <ChevronRight size={12} />
                        <span className={"clickable-if-needed"}>{originTranslation}</span>
                    </BaseBadge>
                </>
            )}
        </BaseIntegrationInject>
    ) : (
        <BaseBadge variant="neutral">
            <span className={"clickable-if-needed"}>{originTranslation}</span>
        </BaseBadge>
    );

    return (
        <>
            {mapping.link && origin.id ? (
                <Box sx={{ ".clickable-if-needed": { textDecoration: "underline" } }}>
                    <Link to={mapping.link(origin.id)}>{originContent}</Link>
                </Box>
            ) : (
                <>{originContent}</>
            )}
        </>
    );
}
